import React, {
  forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';

// Selector de fecha compartido de VLS: reemplaza el <input type="date"> nativo
// (que en cada navegador se ve distinto y en macOS/iOS abre el control gris
// del sistema) por un botón con el mismo aspecto del Select y un calendario
// propio. Patrón "Date Picker Dialog" de ARIA APG: el botón abre un
// role="dialog" con una grilla role="grid" de días; el foco vive en el día
// activo (tabindex itinerante) y el diálogo atrapa el Tab.
//
// ─── Migración desde <input type="date"> (mecánica) ──────────────────────
//   ANTES:
//     <label htmlFor="fi">Fecha inicio</label>
//     <input id="fi" type="date" value={f} min={hoy} required
//            onChange={(e) => setF(e.target.value)}
//            className="w-full px-3 py-2 border rounded-md ..." />
//   DESPUÉS:
//     <label htmlFor="fi">Fecha inicio</label>
//     <DatePicker id="fi" value={f} min={hoy} required
//                 onChange={(iso) => setF(iso)} className="w-full" />
//
//  1. `value`, `min`, `max` y lo que entrega `onChange` son strings ISO
//     'YYYY-MM-DD' (igual que el input nativo). Vacío = ''.
//  2. `onChange` recibe el ISO, no el evento: `e.target.value` → `iso`.
//     Si el handler viejo es genérico (`handleField('x')` que lee
//     e.target.value), pasar `onChange={(iso) => handleField('x')({ target: { value: iso } })}`
//     o, mejor, un setter directo.
//  3. `className` es solo de layout (ancho, margen). El aspecto lo pone el
//     componente: quitar px/py/border/rounded/bg/text-* del input viejo.
//  4. `<label htmlFor={id}>` nombra al botón; `name`/`required`/`form` se
//     comportan como en el Select (input proxy que bloquea el submit nativo).
//  5. `ref` apunta al botón (o al campo de texto con `typeable`).
//
// Otras props: defaultValue (no controlado), disabled, placeholder,
// isDateDisabled(iso) => bool (días no elegibles), clearable (botón
// "Borrar" y tecla Supr/Retroceso), typeable (campo para escribir
// dd/mm/aaaa a mano + botón de calendario), format 'medio' (18 set 2026,
// por defecto) | 'largo' (jueves 18 de setiembre de 2026) | 'numerico'
// (18/09/2026), size 'sm' (40 px) | 'md' (44 px), title (título de la hoja
// en móvil; por defecto el texto del <label>), aria-label, aria-labelledby,
// aria-describedby, data-testid, zIndexClass.
//
// Rango: <DateRangePicker value={{ start, end }} onChange={({ start, end }) => …} />
// (mismo calendario; primer clic = inicio, segundo = fin). Para dos campos
// separados con etiquetas propias, usar dos DatePicker con `min={inicio}`.
//
// Teclado en el calendario (APG): ←/→ día, ↑/↓ semana, Inicio/Fin inicio y
// fin de semana, RePág/AvPág mes, Mayús+RePág/AvPág año, Enter/Espacio
// elige, Escape cierra y devuelve el foco. La semana empieza el lunes.
//
// Sin dependencias. Colores por var(--color-*) con respaldo y medidas en
// estilo en línea (cada app tiene su escala de spacing).

const C = {
  card: 'var(--color-popover, var(--color-card, #FFFFFF))',
  fg: 'var(--color-foreground, #1C1917)',
  muted: 'var(--color-muted, #F5F5F4)',
  mutedFg: 'var(--color-muted-foreground, #78716C)',
  border: 'var(--color-border, #E7E5E4)',
  input: 'var(--color-input, var(--color-border, #E7E5E4))',
  primary: 'var(--color-primary, #0F7A3D)',
  primaryFg: 'var(--color-primary-foreground, #FFFFFF)',
  error: 'var(--color-error, #DC2626)',
  bg: 'var(--color-background, #FFFFFF)',
};
const suave = (pct) => `color-mix(in srgb, ${C.primary} ${pct}%, transparent)`;
const MQ_MOVIL = '(max-width: 639px)';

// ─── Fechas (solo calendario local, sin horas ni zonas) ──────────────────
export const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'setiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
// Lunes primero.
const DIAS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const DIAS_CORTOS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

const pad = (n) => String(n).padStart(2, '0');
export function parseISO(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''));
  if (!m) return null;
  const y = +m[1]; const mo = +m[2] - 1; const d = +m[3];
  const f = new Date(y, mo, d);
  return f.getFullYear() === y && f.getMonth() === mo && f.getDate() === d ? f : null;
}
export const toISO = (f) => `${f.getFullYear()}-${pad(f.getMonth() + 1)}-${pad(f.getDate())}`;
const sumarDias = (f, n) => new Date(f.getFullYear(), f.getMonth(), f.getDate() + n);
const diasDelMes = (y, m) => new Date(y, m + 1, 0).getDate();
function sumarMeses(f, n) {
  const y = f.getFullYear(); const m = f.getMonth() + n;
  const destino = new Date(y, m, 1);
  return new Date(destino.getFullYear(), destino.getMonth(), Math.min(f.getDate(), diasDelMes(destino.getFullYear(), destino.getMonth())));
}
const diaSemana = (f) => (f.getDay() + 6) % 7; // 0 = lunes
const hoyISO = () => toISO(new Date());

export function formatearFecha(iso, formato = 'medio') {
  const f = parseISO(iso);
  if (!f) return '';
  const d = f.getDate(); const m = f.getMonth(); const y = f.getFullYear();
  if (formato === 'numerico') return `${pad(d)}/${pad(m + 1)}/${y}`;
  if (formato === 'largo') return `${DIAS[diaSemana(f)]} ${d} de ${MESES[m]} de ${y}`;
  return `${d} ${MESES_CORTOS[m]} ${y}`;
}

// Acepta d/m/aaaa, d-m-aaaa, d.m.aaaa (año de 2 dígitos = 20xx) y aaaa-mm-dd.
export function interpretarTexto(texto) {
  const t = String(texto ?? '').trim();
  if (!t) return '';
  if (parseISO(t)) return t;
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(t);
  if (!m) return null;
  const y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
  const iso = `${y}-${pad(+m[2])}-${pad(+m[1])}`;
  return parseISO(iso) ? iso : null;
}

function useMovil() {
  const leer = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MQ_MOVIL).matches : false);
  const [movil, setMovil] = useState(leer);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(MQ_MOVIL);
    const on = () => setMovil(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return movil;
}

const Icono = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    {d}
  </svg>
);
const ICal = () => <Icono d={<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />;
const IIzq = () => <Icono d={<polyline points="15 18 9 12 15 6" />} />;
const IDer = () => <Icono d={<polyline points="9 18 15 12 9 6" />} />;
const IIzq2 = () => <Icono d={<><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></>} />;
const IDer2 = () => <Icono d={<><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></>} />;
const IX = () => <Icono size={18} d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} />;

// ─── Calendario (compartido por DatePicker y DateRangePicker) ─────────────
function Calendario({
  baseId, enfocado, setEnfocado, elegido, rango, min, max, isDateDisabled, onElegir, onEscape, movil, listo = true,
}) {
  const gridRef = useRef(null);
  const f = parseISO(enfocado) || new Date();
  const y = f.getFullYear(); const m = f.getMonth();
  const tituloId = `${baseId}-mes`;
  const hoy = hoyISO();

  const fueraDeRango = (iso) => (min && iso < min) || (max && iso > max);
  const noElegible = (iso) => fueraDeRango(iso) || !!isDateDisabled?.(iso);
  // Mover el foco sin salirse de min/max.
  const acotar = (iso) => {
    if (min && iso < min) return min;
    if (max && iso > max) return max;
    return iso;
  };
  const ir = (nueva) => setEnfocado(acotar(toISO(nueva)));

  // El día enfocado recibe el foco real cada vez que cambia.
  useEffect(() => {
    const el = gridRef.current?.querySelector(`[data-iso="${enfocado}"]`);
    el?.focus({ preventScroll: true });
  }, [enfocado, listo]);

  const onKeyDown = (e) => {
    let destino = null;
    switch (e.key) {
      case 'ArrowLeft': destino = sumarDias(f, -1); break;
      case 'ArrowRight': destino = sumarDias(f, 1); break;
      case 'ArrowUp': destino = sumarDias(f, -7); break;
      case 'ArrowDown': destino = sumarDias(f, 7); break;
      case 'Home': destino = sumarDias(f, -diaSemana(f)); break;
      case 'End': destino = sumarDias(f, 6 - diaSemana(f)); break;
      case 'PageUp': destino = sumarMeses(f, e.shiftKey ? -12 : -1); break;
      case 'PageDown': destino = sumarMeses(f, e.shiftKey ? 12 : 1); break;
      case 'Enter': case ' ':
        e.preventDefault(); e.stopPropagation();
        if (!noElegible(enfocado)) onElegir(enfocado);
        return;
      case 'Escape': e.preventDefault(); e.stopPropagation(); onEscape(); return;
      default: return;
    }
    e.preventDefault(); e.stopPropagation();
    ir(destino);
  };

  // Celdas: desde el lunes de la semana del día 1 hasta completar 6 semanas.
  const primero = new Date(y, m, 1);
  const inicio = sumarDias(primero, -diaSemana(primero));
  const semanas = [];
  for (let s = 0; s < 6; s += 1) {
    const fila = [];
    for (let d = 0; d < 7; d += 1) fila.push(sumarDias(inicio, s * 7 + d));
    // La sexta semana solo si todavía tiene días del mes.
    if (s < 5 || fila.some((x) => x.getMonth() === m)) semanas.push(fila);
  }

  const puedeAtras = (n) => !min || toISO(new Date(y, m + n + 1, 0)) >= min;
  const puedeAdelante = (n) => !max || toISO(new Date(y, m + n, 1)) <= max;
  const btnNav = (etiqueta, icono, n, habil) => (
    <button
      type="button" aria-label={etiqueta} disabled={!habil}
      onClick={() => ir(sumarMeses(f, n))}
      className="inline-flex items-center justify-center rounded-lg hover:bg-black/5 disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      style={{ width: movil ? 44 : 36, height: movil ? 44 : 36, color: C.fg }}
    >{icono}</button>
  );
  const celda = movil ? 44 : 38;

  return (
    <div style={{ padding: movil ? '0 12px 8px' : 12 }}>
      <div className="flex items-center" style={{ gap: 2, marginBottom: 8 }}>
        {btnNav('Año anterior', <IIzq2 />, -12, puedeAtras(-12))}
        {btnNav('Mes anterior', <IIzq />, -1, puedeAtras(-1))}
        <h2 id={tituloId} aria-live="polite" className="flex-1 text-center text-sm font-semibold"
          style={{ margin: 0, color: C.fg, textTransform: 'capitalize' }}>
          {MESES[m]} {y}
        </h2>
        {btnNav('Mes siguiente', <IDer />, 1, puedeAdelante(1))}
        {btnNav('Año siguiente', <IDer2 />, 12, puedeAdelante(12))}
      </div>
      <table ref={gridRef} role="grid" aria-labelledby={tituloId} onKeyDown={onKeyDown}
        style={{ borderCollapse: 'collapse', margin: '0 auto', width: movil ? '100%' : undefined }}>
        <thead>
          <tr>
            {DIAS_CORTOS.map((d, i) => (
              <th key={d} scope="col" abbr={DIAS[i]} className="text-xs font-medium"
                style={{ height: 28, width: celda, color: C.mutedFg, padding: 0 }}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {semanas.map((fila) => (
            <tr key={toISO(fila[0])}>
              {fila.map((dia) => {
                const iso = toISO(dia);
                const otroMes = dia.getMonth() !== m;
                const bloqueado = noElegible(iso);
                const sel = rango
                  ? iso === rango.start || iso === rango.end
                  : iso === elegido;
                const dentro = rango && rango.start && rango.end && iso > rango.start && iso < rango.end;
                const esHoy = iso === hoy;
                const esEnfocado = iso === enfocado;
                return (
                  <td key={iso} role="gridcell" aria-selected={sel || undefined} style={{ padding: 1 }}>
                    <button
                      type="button"
                      data-iso={iso}
                      tabIndex={esEnfocado ? 0 : -1}
                      aria-label={`${formatearFecha(iso, 'largo')}${esHoy ? ', hoy' : ''}`}
                      aria-current={esHoy ? 'date' : undefined}
                      aria-disabled={bloqueado || undefined}
                      onClick={() => { setEnfocado(iso); if (!bloqueado) onElegir(iso); }}
                      className="inline-flex items-center justify-center rounded-lg text-sm tabular-nums focus:outline-none"
                      style={{
                        width: movil ? '100%' : celda, height: celda, minWidth: 36,
                        cursor: bloqueado ? 'not-allowed' : 'pointer',
                        color: sel ? C.primaryFg : (otroMes ? C.mutedFg : C.fg),
                        background: sel ? C.primary : (dentro ? suave(12) : (esEnfocado ? C.muted : 'transparent')),
                        opacity: bloqueado ? 0.35 : (otroMes && !sel ? 0.6 : 1),
                        textDecoration: bloqueado ? 'line-through' : 'none',
                        fontWeight: esHoy || sel ? 600 : 400,
                        boxShadow: [
                          esHoy && !sel ? `inset 0 0 0 1.5px ${C.primary}` : null,
                          esEnfocado ? `0 0 0 2px ${C.card}, 0 0 0 4px ${suave(55)}` : null,
                        ].filter(Boolean).join(', ') || 'none',
                      }}
                    >
                      {dia.getDate()}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Envoltura común: botón/campo + popover o hoja ────────────────────────
function useSelectorFecha({ disabled, movil }) {
  const triggerRef = useRef(null);
  const zonaRef = useRef(null); // campo completo (con typeable: texto + botón)
  const panelRef = useRef(null);
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState(null);

  const cerrar = useCallback((devolverFoco = true) => {
    setAbierto(false);
    if (devolverFoco) triggerRef.current?.focus();
  }, []);

  const medir = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth; const vh = window.innerHeight;
    const ancho = Math.min(312, vw - 16);
    const alto = 380;
    const abajo = vh - r.bottom - 8; const arriba = r.top - 8;
    const haciaArriba = abajo < alto && arriba > abajo;
    const left = Math.max(8, Math.min(r.left, vw - ancho - 8));
    setPos({ left, width: ancho, ...(haciaArriba ? { bottom: vh - r.top + 4 } : { top: r.bottom + 4 }) });
  }, []);

  useLayoutEffect(() => {
    if (!abierto || movil) return undefined;
    medir();
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [abierto, movil, medir]);

  useEffect(() => {
    if (!abierto) return undefined;
    const afuera = (e) => {
      if ((zonaRef.current || triggerRef.current)?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      cerrar(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('touchstart', afuera, { passive: true });
    let prev;
    if (movil) { prev = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('touchstart', afuera);
      if (movil) document.body.style.overflow = prev;
    };
  }, [abierto, movil, cerrar]);

  return {
    triggerRef, zonaRef, panelRef, abierto, pos, cerrar,
    abrir: () => { if (!disabled) setAbierto(true); },
  };
}

// Tab/Mayús+Tab dan la vuelta dentro del diálogo (APG: diálogo modal).
function atraparTab(e, panel) {
  if (e.key !== 'Tab' || !panel) return;
  const focusables = [...panel.querySelectorAll('button:not([disabled]), input:not([disabled])')]
    .filter((el) => el.tabIndex >= 0);
  if (!focusables.length) return;
  const i = focusables.indexOf(document.activeElement);
  // stopPropagation: que el focus trap del modal que lo contiene (escucha en
  // document) no se lleve el foco de vuelta al modal.
  e.preventDefault();
  e.stopPropagation();
  const n = e.shiftKey ? (i <= 0 ? focusables.length - 1 : i - 1) : (i + 1) % focusables.length;
  focusables[n].focus();
}

function tituloDe({ title, ariaLabel, ariaLabelledBy, id, respaldo }) {
  if (typeof document === 'undefined') return respaldo;
  let t = title || ariaLabel || '';
  if (!t && ariaLabelledBy) t = ariaLabelledBy.split(/\s+/).map((x) => document.getElementById(x)?.textContent || '').join(' ').trim();
  if (!t && id) {
    const sel = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    t = document.querySelector(`label[for="${sel}"]`)?.textContent?.trim() || '';
  }
  return t || respaldo;
}

function Panel({
  movil, abierto, pos, panelRef, baseId, titulo, cerrar, zIndexClass, children, pie,
}) {
  if (!abierto || typeof document === 'undefined') return null;
  const zStyle = zIndexClass ? undefined : 'var(--vls-select-z, 1000)';
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cerrar(true); return; }
    atraparTab(e, panelRef.current);
  };
  const cuerpo = (
    <>
      {children}
      {pie}
    </>
  );
  const panel = movil ? (
    <div className={`fixed inset-0 flex flex-col justify-end ${zIndexClass || ''}`} style={{ zIndex: zStyle, margin: 0 }}>
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onMouseDown={() => cerrar(true)} />
      <div
        ref={panelRef}
        role="dialog" aria-modal="true" aria-labelledby={`${baseId}-titulo`}
        data-vls-datepicker-panel="hoja"
        onKeyDown={onKeyDown}
        className="relative w-full flex flex-col rounded-t-2xl shadow-2xl"
        style={{ background: C.card, borderTop: `1px solid ${C.border}`, maxHeight: '90dvh', overflowY: 'auto', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
      >
        <div className="flex justify-center" style={{ paddingTop: 8, paddingBottom: 4 }} aria-hidden="true">
          <div className="rounded-full" style={{ width: 40, height: 5, background: C.border }} />
        </div>
        <div className="flex items-center justify-between" style={{ gap: 12, padding: '4px 8px 4px 16px' }}>
          <h2 id={`${baseId}-titulo`} className="text-base font-semibold truncate" style={{ color: C.fg, margin: 0 }}>{titulo}</h2>
          <button type="button" aria-label="Cerrar calendario" onClick={() => cerrar(true)}
            className="flex items-center justify-center rounded-lg hover:bg-black/5"
            style={{ width: 44, height: 44, color: C.mutedFg, flexShrink: 0 }}><IX /></button>
        </div>
        {cuerpo}
      </div>
    </div>
  ) : (
    <div
      ref={panelRef}
      role="dialog" aria-modal="true" aria-label={titulo}
      data-vls-datepicker-panel="popover"
      onKeyDown={onKeyDown}
      className={`fixed border rounded-xl ${zIndexClass || ''}`}
      style={{
        zIndex: zStyle, margin: 0, background: C.card, borderColor: C.border,
        boxShadow: '0 10px 30px -8px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.06)',
        left: pos?.left ?? -9999, top: pos?.top, bottom: pos?.bottom, width: pos?.width,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {cuerpo}
    </div>
  );
  return createPortal(panel, document.body);
}

const botonPie = (texto, onClick, movil, disabled) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className="text-sm font-medium rounded-lg hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    style={{ color: C.primary, padding: '0 12px', height: movil ? 44 : 36 }}>{texto}</button>
);

function estiloCampo({ alto, disabled, invalido, abierto, vacio }) {
  return {
    minWidth: 0, height: alto, padding: '0 10px 0 12px', gap: 8,
    background: disabled ? C.muted : C.bg,
    borderColor: invalido ? C.error : (abierto ? C.primary : C.input),
    color: vacio ? C.mutedFg : C.fg,
    opacity: disabled ? 0.65 : 1,
    boxShadow: abierto ? `0 0 0 3px ${suave(15)}` : undefined,
    transition: 'border-color 150ms ease-out, box-shadow 150ms ease-out',
  };
}

function ProxyForm({ required, name, form, valor, onInvalid }) {
  if (required) {
    return (
      <input tabIndex={-1} aria-hidden="true" name={name} form={form} required value={valor}
        onChange={() => {}} onInvalid={(e) => { e.preventDefault(); onInvalid(); }}
        style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none', border: 0, padding: 0 }} />
    );
  }
  return name ? <input type="hidden" name={name} form={form} value={valor} /> : null;
}

// ─── DatePicker ───────────────────────────────────────────────────────────
const DatePicker = forwardRef(function DatePicker({
  value, defaultValue, onChange, min, max, isDateDisabled,
  disabled = false, required = false, id, name, form, className = '', size = 'md',
  placeholder = 'Elegir fecha', format = 'medio', clearable = false, typeable = false,
  title, zIndexClass,
  'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
}, ref) {
  const baseId = useId().replace(/:/g, '');
  const errorId = `${baseId}-error`;
  const movil = useMovil();
  const s = useSelectorFecha({ disabled, movil });
  const textoRef = useRef(null);
  useImperativeHandle(ref, () => (typeable ? textoRef.current : s.triggerRef.current));

  const controlado = value !== undefined;
  const [interno, setInterno] = useState(defaultValue ?? '');
  const actual = (controlado ? value : interno) || '';
  const valido = parseISO(actual) ? actual : '';
  const [enfocado, setEnfocado] = useState(valido || hoyISO());
  const [invalido, setInvalido] = useState(false);
  const [error, setError] = useState('');
  const [texto, setTexto] = useState(formatearFecha(valido, 'numerico'));

  useEffect(() => { setTexto(formatearFecha(valido, 'numerico')); }, [valido]);

  const noElegible = (iso) => (min && iso < min) || (max && iso > max) || !!isDateDisabled?.(iso);

  const emitir = (iso) => {
    if (!controlado) setInterno(iso);
    setInvalido(false); setError('');
    onChange?.(iso);
  };

  const abrir = () => {
    let ini = valido || hoyISO();
    if (min && ini < min) ini = min;
    if (max && ini > max) ini = max;
    setEnfocado(ini);
    s.abrir();
  };

  const elegir = (iso) => { emitir(iso); s.cerrar(true); };

  const confirmarTexto = () => {
    const iso = interpretarTexto(texto);
    if (iso === '') { if (valido) emitir(''); return; }
    if (iso === null) { setError('Escriba la fecha como dd/mm/aaaa.'); return; }
    if (noElegible(iso)) { setError('Esa fecha no está disponible.'); return; }
    if (iso !== valido) emitir(iso); else setError('');
    setTexto(formatearFecha(iso, 'numerico'));
  };

  const alto = size === 'sm' ? 40 : 44;
  const describedBy = [ariaDescribedBy, (invalido || error) ? errorId : null].filter(Boolean).join(' ') || undefined;
  const titulo = s.abierto ? tituloDe({ title, ariaLabel, ariaLabelledBy, id, respaldo: 'Elegir fecha' }) : '';
  const hoy = hoyISO();

  const pie = (
    <div className="flex items-center justify-between" style={{ gap: 8, padding: '0 8px 8px', borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
      {botonPie('Hoy', () => { if (!noElegible(hoy)) elegir(hoy); else setEnfocado(hoy); }, movil, noElegible(hoy))}
      {clearable && valido ? botonPie('Borrar', () => elegir(''), movil) : <span />}
    </div>
  );

  const onKeyTrigger = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); abrir(); return; }
    if (clearable && (e.key === 'Delete' || e.key === 'Backspace') && valido && !typeable) { e.preventDefault(); emitir(''); }
  };

  const campo = typeable ? (
    <div
      ref={s.zonaRef}
      className={`inline-flex items-center text-sm rounded-lg border ${className}`}
      style={{ ...estiloCampo({ alto, disabled, invalido: invalido || !!error, abierto: s.abierto, vacio: !texto }), padding: '0 4px 0 12px' }}
      data-vls-datepicker=""
    >
      <input
        ref={textoRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="dd/mm/aaaa"
        value={texto}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        aria-invalid={(invalido || !!error) || undefined}
        data-testid={testId}
        onChange={(e) => { setTexto(e.target.value); setError(''); }}
        onBlur={confirmarTexto}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); confirmarTexto(); }
          if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); abrir(); }
        }}
        className="flex-1 min-w-0 bg-transparent tabular-nums focus:outline-none"
        style={{ color: C.fg, border: 0, padding: 0, boxShadow: 'none', fontSize: movil ? 16 : undefined }}
      />
      <button
        ref={s.triggerRef}
        type="button"
        aria-label={valido ? `Elegir fecha, ${formatearFecha(valido, 'largo')}` : 'Elegir fecha'}
        aria-haspopup="dialog"
        aria-expanded={s.abierto}
        disabled={disabled}
        onClick={() => (s.abierto ? s.cerrar(true) : abrir())}
        className="inline-flex items-center justify-center rounded-md hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        style={{ width: 36, height: 36, color: C.mutedFg, flexShrink: 0 }}
      ><ICal /></button>
    </div>
  ) : (
    <button
      ref={s.triggerRef}
      type="button"
      id={id}
      aria-haspopup="dialog"
      aria-expanded={s.abierto}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={[describedBy, `${baseId}-valor`].filter(Boolean).join(' ')}
      aria-required={required || undefined}
      aria-invalid={invalido || undefined}
      disabled={disabled}
      data-testid={testId}
      data-vls-datepicker=""
      data-value={valido}
      onClick={() => (s.abierto ? s.cerrar(true) : abrir())}
      onKeyDown={onKeyTrigger}
      className={`inline-flex items-center text-sm text-left rounded-lg border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      style={estiloCampo({ alto, disabled, invalido, abierto: s.abierto, vacio: !valido })}
    >
      <span id={`${baseId}-valor`} className="flex-1 min-w-0 truncate tabular-nums">
        {valido ? formatearFecha(valido, format) : placeholder}
      </span>
      <span style={{ color: C.mutedFg, display: 'inline-flex' }}><ICal /></span>
    </button>
  );

  return (
    <>
      {campo}
      <ProxyForm required={required} name={name} form={form} valor={valido}
        onInvalid={() => { setInvalido(true); (typeable ? textoRef.current : s.triggerRef.current)?.focus(); }} />
      {(invalido || error) && (
        <span id={errorId} role="alert" className="block text-xs" style={{ color: C.error, marginTop: 4 }}>
          {error || 'Elija una fecha.'}
        </span>
      )}
      <Panel movil={movil} abierto={s.abierto} pos={s.pos} panelRef={s.panelRef} baseId={baseId}
        titulo={titulo} cerrar={s.cerrar} zIndexClass={zIndexClass} pie={pie}>
        <Calendario baseId={baseId} listo={movil || !!s.pos} enfocado={enfocado} setEnfocado={setEnfocado} elegido={valido}
          min={min} max={max} isDateDisabled={isDateDisabled} onElegir={elegir}
          onEscape={() => s.cerrar(true)} movil={movil} />
      </Panel>
    </>
  );
});

// ─── DateRangePicker ──────────────────────────────────────────────────────
// value = { start: 'YYYY-MM-DD' | '', end: 'YYYY-MM-DD' | '' }. onChange se
// llama con el rango completo al elegir el fin (y con { start, end: '' }
// al elegir el inicio, para que la pantalla pueda reaccionar). Un rango no
// puede atravesar un día deshabilitado (isDateDisabled).
export const DateRangePicker = forwardRef(function DateRangePicker({
  value, defaultValue, onChange, min, max, isDateDisabled,
  disabled = false, required = false, id, name, form, className = '', size = 'md',
  placeholder = 'Elegir rango', format = 'medio', clearable = false, title, zIndexClass,
  'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
}, ref) {
  const baseId = useId().replace(/:/g, '');
  const errorId = `${baseId}-error`;
  const movil = useMovil();
  const s = useSelectorFecha({ disabled, movil });
  useImperativeHandle(ref, () => s.triggerRef.current);

  const controlado = value !== undefined;
  const [interno, setInterno] = useState(defaultValue ?? { start: '', end: '' });
  const crudo = (controlado ? value : interno) || {};
  const rango = { start: parseISO(crudo.start) ? crudo.start : '', end: parseISO(crudo.end) ? crudo.end : '' };
  const [borrador, setBorrador] = useState(null); // inicio elegido, falta el fin
  const [enfocado, setEnfocado] = useState(rango.start || hoyISO());
  const [invalido, setInvalido] = useState(false);

  const emitir = (r) => {
    if (!controlado) setInterno(r);
    setInvalido(false);
    onChange?.(r);
  };

  const abrir = () => {
    let ini = rango.start || hoyISO();
    if (min && ini < min) ini = min;
    if (max && ini > max) ini = max;
    setEnfocado(ini); setBorrador(null);
    s.abrir();
  };

  const cruzaBloqueado = (a, b) => {
    if (!isDateDisabled) return false;
    for (let d = parseISO(a); toISO(d) <= b; d = sumarDias(d, 1)) if (isDateDisabled(toISO(d))) return true;
    return false;
  };

  const elegir = (iso) => {
    if (!borrador) { setBorrador(iso); emitir({ start: iso, end: '' }); return; }
    const [a, b] = iso < borrador ? [iso, borrador] : [borrador, iso];
    if (cruzaBloqueado(a, b)) { setBorrador(iso); emitir({ start: iso, end: '' }); return; }
    setBorrador(null);
    emitir({ start: a, end: b });
    s.cerrar(true);
  };

  const visible = borrador ? { start: borrador, end: '' } : rango;
  const texto = rango.start
    ? `${formatearFecha(rango.start, format)} – ${rango.end ? formatearFecha(rango.end, format) : '…'}`
    : placeholder;
  const alto = size === 'sm' ? 40 : 44;
  const titulo = s.abierto ? tituloDe({ title, ariaLabel, ariaLabelledBy, id, respaldo: 'Elegir rango de fechas' }) : '';
  const describedBy = [ariaDescribedBy, invalido ? errorId : null, `${baseId}-valor`].filter(Boolean).join(' ');

  const pie = (
    <div className="flex items-center justify-between" style={{ gap: 8, padding: '8px 8px 8px 16px', borderTop: `1px solid ${C.border}` }}>
      <span className="text-xs" aria-live="polite" style={{ color: C.mutedFg }}>
        {borrador ? 'Elija la fecha final.' : 'Elija la fecha inicial.'}
      </span>
      {clearable && (rango.start || borrador) ? botonPie('Borrar', () => { setBorrador(null); emitir({ start: '', end: '' }); s.cerrar(true); }, movil) : <span />}
    </div>
  );

  return (
    <>
      <button
        ref={s.triggerRef}
        type="button"
        id={id}
        aria-haspopup="dialog"
        aria-expanded={s.abierto}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        aria-invalid={invalido || undefined}
        disabled={disabled}
        data-testid={testId}
        data-vls-datepicker="rango"
        onClick={() => (s.abierto ? s.cerrar(true) : abrir())}
        className={`inline-flex items-center text-sm text-left rounded-lg border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
        style={estiloCampo({ alto, disabled, invalido, abierto: s.abierto, vacio: !rango.start })}
      >
        <span id={`${baseId}-valor`} className="flex-1 min-w-0 truncate tabular-nums">{texto}</span>
        <span style={{ color: C.mutedFg, display: 'inline-flex' }}><ICal /></span>
      </button>
      {name && <input type="hidden" name={`${name}_desde`} form={form} value={rango.start} />}
      {name && <input type="hidden" name={`${name}_hasta`} form={form} value={rango.end} />}
      {required && (
        <ProxyForm required valor={rango.start && rango.end ? 'ok' : ''} form={form}
          onInvalid={() => { setInvalido(true); s.triggerRef.current?.focus(); }} />
      )}
      {invalido && (
        <span id={errorId} role="alert" className="block text-xs" style={{ color: C.error, marginTop: 4 }}>
          Elija las dos fechas.
        </span>
      )}
      <Panel movil={movil} abierto={s.abierto} pos={s.pos} panelRef={s.panelRef} baseId={baseId}
        titulo={titulo} cerrar={s.cerrar} zIndexClass={zIndexClass} pie={pie}>
        <Calendario baseId={baseId} listo={movil || !!s.pos} enfocado={enfocado} setEnfocado={setEnfocado} rango={visible}
          min={min} max={max} isDateDisabled={isDateDisabled} onElegir={elegir}
          onEscape={() => s.cerrar(true)} movil={movil} />
      </Panel>
    </>
  );
});

export default DatePicker;
